import { useEffect, useRef, forwardRef } from "react";
import gsap from "gsap";
import { VideoWithShimmer } from "@/components/ui/video-with-shimmer";

interface HeroImageProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  videoSrc?: string;
  alt?: string;
}

const HeroImage = forwardRef<HTMLDivElement, HeroImageProps>(
  ({ className, src, videoSrc, alt, style, ...props }, ref) => {
    const ContentRef = useRef<HTMLImageElement | HTMLVideoElement>(null);
    const rotateXTo = useRef<gsap.QuickToFunc | null>(null);
    const rotateYTo = useRef<gsap.QuickToFunc | null>(null);
    const rectRef = useRef<DOMRect | null>(null);

    useEffect(() => {
      if (!ContentRef || !ContentRef.current) return;
      const el = ContentRef.current;

      rotateXTo.current = gsap.quickTo(el, "rotationX", {
        duration: 0.5,
        ease: "power3.out",
      });
      rotateYTo.current = gsap.quickTo(el, "rotationY", {
        duration: 0.5,
        ease: "power3.out",
      });

      gsap.set(el, {
        transformPerspective: 800,
        transformOrigin: "center center",
        willChange: "transform",
      });
    }, []);

    const handleMouseEnter = () => {
      if (!ContentRef || !ContentRef.current) return;
      rectRef.current = ContentRef.current.getBoundingClientRect();
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        !rectRef.current ||
        !rotateXTo.current ||
        !rotateYTo.current
      )
        return;

      const rect = rectRef.current;

      const nx = gsap.utils.clamp(
        0.05,
        0.95,
        (e.clientX - rect.left) / rect.width
      );
      const ny = gsap.utils.clamp(
        0.05,
        0.95,
        (e.clientY - rect.top) / rect.height
      );

      const referenceWidth = 400;
      const sizeFactor = Math.sqrt(referenceWidth / Math.max(rect.width, 100));
      const maxRotation = Math.min(10 * sizeFactor, 25);

      const rotateX = gsap.utils.interpolate(maxRotation, -maxRotation, ny);
      const rotateY = gsap.utils.interpolate(-maxRotation, maxRotation, nx);

      rotateXTo.current(rotateX);
      rotateYTo.current(rotateY);
    };

    const handleMouseLeave = () => {
      rectRef.current = null;
      rotateXTo.current?.(0);
      rotateYTo.current?.(0);
    };

    return (
      <div 
        ref={ref} 
        className={className} 
        style={style} 
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        {videoSrc ? (
          <VideoWithShimmer
            ref={ContentRef as any}
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            containerClassName="w-full h-full"
            className="rounded-sm 2k:rounded-lg 4k:rounded-xl w-full h-full object-cover"
          />
        ) : (
          <img
            ref={ContentRef as any}
            src={src}
            alt={alt}
            className="rounded-sm 2k:rounded-lg 4k:rounded-xl w-full h-full object-cover"
          />
        )}
      </div>
    );
  }
);

HeroImage.displayName = "HeroImage";
export default HeroImage;